import React, { useEffect, useRef, useState } from "react";
import { Box, Spinner, Heading } from "@chakra-ui/react";
import axios from "axios";
import echarts from "./lib";

// Year-over-Year growth — polarity keeps its semantic red/green (brightened);
// the section's purple stays out of the way of meaning.
const GREEN = "rgba(26,164,124,0.95)";
const GREEN_FADE = "rgba(26,164,124,0.3)";
const RED = "rgba(224,82,72,0.95)";
const RED_FADE = "rgba(224,82,72,0.35)";

// `region` is optional — when set (CHK region tabs), the region endpoint and
// request body are used, exactly as the old Chart6Region.js did.
const Chart6 = ({ supplier, retailer, region }) => {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let disposed = false;
    const controller = new AbortController();

    const initChart = async () => {
      if (!containerRef.current) return;
      setLoading(true);
      try {
        const res = await axios.post(
          `${process.env.REACT_APP_API_URL}/rep_sales/${region ? "chart6_chk_region" : "chart6"}`,
          region
            ? { supplier, retailer, region, userid: localStorage.getItem("userid") }
            : { supplier, retailer, userid: localStorage.getItem("userid") },
          { signal: controller.signal }
        );

        // Identical transforms to the Chart.js version — note the en-GB
        // locale (unlike Chart4/5) and raw values; sign split at < 0.
        const raw = res.data.data;
        const labels = raw.map((i) =>
          new Date(i.date).toLocaleDateString("en-GB", { year: "numeric", month: "short" })
        );
        const values = raw.map((i) => i.value);

        if (disposed || !containerRef.current) return;

        const chart = echarts.init(containerRef.current);
        chartRef.current = chart;
        chart.setOption({
          grid: { left: 8, right: 14, top: 26, bottom: 4, containLabel: true },
          xAxis: {
            type: "category",
            data: labels,
            axisLabel: { color: "#64798A", fontSize: 10.5, rotate: 45 },
            axisLine: { lineStyle: { color: "#93A5B1" } }, // the zero line matters here
            axisTick: { show: false },
          },
          yAxis: {
            type: "value",
            axisLabel: { color: "#64798A", fontSize: 11, formatter: (v) => `${v}%` },
            splitLine: { lineStyle: { color: "rgba(229,231,235,0.5)", type: [3, 3] } },
          },
          tooltip: {
            trigger: "axis",
            axisPointer: { type: "shadow", shadowStyle: { color: "rgba(138,99,210,0.07)" } },
            confine: true,
            backgroundColor: "#ffffff",
            borderColor: "#E2E9EE",
            borderWidth: 1,
            padding: [8, 12],
            textStyle: { color: "#1D2F3A", fontSize: 12 },
            extraCssText: "box-shadow: 0 6px 18px rgba(29,47,58,0.14); border-radius: 8px;",
            formatter: (params) => {
              const p = params[0];
              const up = p.value >= 0;
              return `${p.name}<br/><b style="color:${up ? "#12775F" : "#BF3F38"}">${Number(
                p.value
              ).toFixed(1)}%</b> vs last year`;
            },
          },
          series: [
            {
              type: "bar",
              barWidth: "62%",
              data: values.map((v) => ({
                value: v,
                itemStyle: {
                  borderRadius: v < 0 ? [0, 0, 5, 5] : [5, 5, 0, 0],
                  color: {
                    type: "linear",
                    x: 0,
                    y: 0,
                    x2: 0,
                    y2: 1,
                    colorStops:
                      v < 0
                        ? [
                            { offset: 0, color: RED_FADE },
                            { offset: 1, color: RED },
                          ]
                        : [
                            { offset: 0, color: GREEN },
                            { offset: 1, color: GREEN_FADE },
                          ],
                  },
                },
                label: { position: v < 0 ? "bottom" : "top" },
              })),
              emphasis: {
                itemStyle: { shadowBlur: 10, shadowColor: "rgba(29,47,58,0.25)" },
              },
              label: {
                show: true,
                formatter: (p) => `${Number(p.value).toFixed(1)}%`,
                color: "#4A5D6B",
                fontSize: 10.5,
                fontWeight: 700,
                fontFamily: "Inter, sans-serif",
              },
              labelLayout: { hideOverlap: true },
              animationDuration: 800,
              animationDelay: (idx) => idx * 40,
              animationEasing: "cubicOut",
            },
          ],
        });
      } catch (error) {
        if (!axios.isCancel(error)) console.error("chart6 fetch failed", error);
      } finally {
        if (!disposed) setLoading(false);
      }
    };

    initChart();

    const ro = new ResizeObserver(() => {
      if (chartRef.current) chartRef.current.resize();
    });
    if (containerRef.current) ro.observe(containerRef.current);

    return () => {
      disposed = true;
      controller.abort();
      ro.disconnect();
      if (chartRef.current) {
        chartRef.current.dispose();
        chartRef.current = null;
      }
    };
  }, [supplier, retailer, region]);

  return (
    <Box height="100%" display="flex" flexDirection="column" position="relative">
      <Heading size="xs" color="gray.600" mb={2} fontWeight="600">
        {region ? `Year-over-Year Growth (${region})` : "Year-over-Year Growth"}
      </Heading>
      <Box flex="1" position="relative">
        <div
          ref={containerRef}
          style={{ width: "100%", height: "100%", overflow: "hidden" }}
        ></div>
        {loading && (
          <Spinner
            position="absolute"
            top="50%"
            left="50%"
            transform="translate(-50%, -50%)"
          />
        )}
      </Box>
    </Box>
  );
};

export default Chart6;
